import React, { forwardRef, useImperativeHandle, ReactNode } from "react";
import {
  useForm,
  Resolver,
  SubmitHandler,
  FieldValues,
  FormProvider,
  UseFormReturn,
} from "react-hook-form";

interface VerticalFormProps<TFormValues extends FieldValues> {
  defaultValues?: any;
  resolver?: Resolver<TFormValues>;
  children?: ReactNode;
  onSubmit: SubmitHandler<TFormValues>;
  formClass?: string;
}

export interface VerticalFormHandle<TFormValues extends FieldValues> {
  reset: UseFormReturn<TFormValues>["reset"];
  methods: UseFormReturn<TFormValues>;
}

const VerticalFormInner = <
  TFormValues extends Record<string, any> = Record<string, any>
>(
  {
    defaultValues,
    resolver,
    children,
    onSubmit,
    formClass,
  }: VerticalFormProps<TFormValues>,
  ref: React.Ref<VerticalFormHandle<TFormValues>>
) => {
  const methods = useForm<TFormValues>({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  // Expose reset and methods to parent
  useImperativeHandle(
    ref,
    () => ({
      reset: (value: any) => {
        console.log("Resetting form with value:", value);
        reset(value);
      },
      methods,
    }),
    [methods, reset]
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className={formClass} noValidate>
        {Array.isArray(children)
          ? children.map((child) =>
              child?.props?.name
                ? React.createElement(child.type, {
                    ...child.props,
                    key: child.props.name,
                    register,
                    errors,
                    control,
                  })
                : child
            )
          : children}
      </form>
    </FormProvider>
  );
};

const VerticalForm = forwardRef(VerticalFormInner) as <
  TFormValues extends FieldValues = FieldValues
>(
  props: VerticalFormProps<TFormValues> & {
    ref?: React.Ref<VerticalFormHandle<TFormValues>>;
  }
) => ReturnType<typeof VerticalFormInner>;

export default VerticalForm;
